/**
 * SPRINT 3A - Diagnóstico de Plataforma para Testes Reais
 * 
 * Este módulo coleta métricas detalhadas de cada interação de voz
 * para validar que >95% das conversas terminam por eventos naturais.
 */

class VoiceDiagnostics {
  constructor() {
    this.sessions = [];
    this.currentSession = null;
    this.enabled = true;
  }

  /**
   * Inicia uma nova sessão de diagnóstico
   */
  startSession(platform) {
    this.currentSession = {
      id: `session_${Date.now()}`,
      platform: platform || this._detectPlatform(),
      startTime: Date.now(),
      interactions: [],
      summary: null,
    };
    
    console.log(`[VoiceDiagnostics] Session started: ${this.currentSession.id} on ${this.currentSession.platform}`);
    return this.currentSession.id;
  }

  /**
   * Inicia uma nova interação de voz
   */
  startInteraction() {
    if (!this.currentSession) {
      this.startSession();
    }
    
    const interaction = {
      id: `int_${Date.now()}`,
      startTime: Date.now(),
      events: [],
      metrics: {
        micOpened: false,
        micOpenTime: null,
        audioCapturing: false,
        audioStartTime: null,
        sttResults: [],
        turnDecision: null,
        llmResponse: null,
        ttsPlayed: false,
        bargeInTriggered: false,
        endSignal: null,  // 'speechEnd', 'audioEnd', 'sttStable', 'fallback'
        totalTime: null,
      },
      errors: [],
    };
    
    this.currentSession.interactions.push(interaction);
    console.log(`[VoiceDiagnostics] Interaction started: ${interaction.id}`);
    return interaction;
  }

  /**
   * Registra evento com timestamp
   */
  logEvent(eventType, data = {}) {
    if (!this.currentSession || this.currentSession.interactions.length === 0) return;
    
    const interaction = this.currentSession.interactions[this.currentSession.interactions.length - 1];
    const event = {
      type: eventType,
      timestamp: Date.now(),
      timeSinceStart: Date.now() - interaction.startTime,
      data,
    };
    
    interaction.events.push(event);
    
    // Atualizar métricas específicas
    switch (eventType) {
      case 'MIC_OPENED':
        interaction.metrics.micOpened = true;
        interaction.metrics.micOpenTime = event.timeSinceStart;
        break;
      case 'AUDIO_CAPTURE_START':
        interaction.metrics.audioCapturing = true;
        interaction.metrics.audioStartTime = event.timeSinceStart;
        break;
      case 'STT_RESULT':
        interaction.metrics.sttResults.push({
          transcript: data.transcript,
          isFinal: data.isFinal,
          confidence: data.confidence,
          time: event.timeSinceStart,
        });
        break;
      case 'TURN_DECISION':
        interaction.metrics.turnDecision = {
          reason: data.reason,
          scores: data.scores,
          totalScore: data.totalScore,
          time: event.timeSinceStart,
        };
        // Identificar qual sinal encerrou
        interaction.metrics.endSignal = this._identifyEndSignal(data);
        break;
      case 'LLM_RESPONSE':
        interaction.metrics.llmResponse = {
          length: data.length,
          time: event.timeSinceStart,
        };
        break;
      case 'TTS_PLAYED':
        interaction.metrics.ttsPlayed = true;
        break;
      case 'BARGE_IN':
        interaction.metrics.bargeInTriggered = true;
        break;
      case 'ERROR':
        interaction.errors.push(data);
        break;
      case 'INTERACTION_END':
        interaction.metrics.totalTime = event.timeSinceStart;
        break;
    }
    
    console.log(`[VoiceDiagnostics] ${eventType}:`, data);
  }

  /**
   * Identifica qual sinal encerrou a interação
   */
  _identifyEndSignal(turnDecision) {
    const { scores, reason } = turnDecision;
    
    // Ordem de prioridade para identificar sinal decisivo
    if (reason === 'FALLBACK_TIMER') return 'fallback';
    if (scores?.speechEndEvent > 0) return 'speechEnd';
    if (scores?.audioEndEvent > 0) return 'audioEnd';
    if (scores?.sttFinalStable > 0) return 'sttStable';
    
    // Fallback baseado no reason
    if (reason?.includes('SPEECH_END')) return 'speechEnd';
    if (reason?.includes('AUDIO_END')) return 'audioEnd';
    if (reason?.includes('STT_FINAL_STABLE') || reason?.includes('STT_STABLE')) return 'sttStable';
    if (reason?.includes('FALLBACK')) return 'fallback';
    
    return 'unknown';
  }

  /**
   * Finaliza a sessão atual e gera resumo
   */
  endSession() {
    if (!this.currentSession) return null;
    
    const session = this.currentSession;
    session.endTime = Date.now();
    session.duration = session.endTime - session.startTime;
    
    // Calcular estatísticas
    const stats = this._calculateSessionStats(session);
    session.summary = stats;
    
    this.sessions.push(session);
    this.currentSession = null;
    
    console.log(`[VoiceDiagnostics] Session ended. Summary:`, stats);
    return session;
  }

  /**
   * Calcula estatísticas da sessão
   */
  _calculateSessionStats(session) {
    const interactions = session.interactions.filter(i => i.metrics.endSignal);
    const total = interactions.length;
    
    if (total === 0) {
      return { total: 0, naturalPercentage: '0.0', meetsTarget: false };
    }
    
    const bySignal = {
      speechEnd: 0,
      audioEnd: 0,
      sttStable: 0,
      fallback: 0,
      unknown: 0,
    };
    
    interactions.forEach(i => {
      const signal = i.metrics.endSignal;
      bySignal[signal] = (bySignal[signal] || 0) + 1;
    });
    
    const natural = bySignal.speechEnd + bySignal.audioEnd + bySignal.sttStable;
    const fallback = bySignal.fallback;
    const naturalPercentage = ((natural / total) * 100).toFixed(1);
    const fallbackPercentage = ((fallback / total) * 100).toFixed(1);
    
    return {
      total,
      natural,
      fallback,
      naturalPercentage,
      fallbackPercentage,
      meetsTarget: parseFloat(naturalPercentage) >= 95,
      breakdown: bySignal,
      avgTotalTime: this._calculateAvg(interactions, i => i.metrics.totalTime),
      avgMicOpenTime: this._calculateAvg(interactions, i => i.metrics.micOpenTime),
    };
  }

  _calculateAvg(arr, getter) {
    const values = arr.map(getter).filter(v => v !== null && v !== undefined);
    if (values.length === 0) return 0;
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  }

  /**
   * Detecta plataforma atual
   */
  _detectPlatform() {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isSamsung = /SamsungBrowser/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && !/Edge/i.test(ua);
    const isEdge = /Edg/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !isChrome;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    
    if (isPWA) {
      if (isIOS) return 'pwa-ios';
      if (isAndroid) return 'pwa-android';
      return 'pwa-desktop';
    }
    if (isIOS && isSafari) return 'ios-safari';
    if (isIOS && isChrome) return 'ios-chrome';
    if (isAndroid && isSamsung) return 'android-samsung';
    if (isAndroid && isChrome) return 'android-chrome';
    if (isEdge) return 'desktop-edge';
    if (isChrome) return 'desktop-chrome';
    if (isSafari) return 'desktop-safari';
    
    return 'unknown';
  }

  /**
   * Retorna todas as sessões para análise
   */
  getAllSessions() {
    return this.sessions;
  }

  /**
   * Retorna estatísticas agregadas de todas as sessões por plataforma
   */
  getAggregatedStats() {
    const byPlatform = {};
    
    this.sessions.forEach(session => {
      const platform = session.platform;
      if (!byPlatform[platform]) {
        byPlatform[platform] = {
          sessions: 0,
          interactions: 0,
          natural: 0,
          fallback: 0,
          breakdown: { speechEnd: 0, audioEnd: 0, sttStable: 0, fallback: 0 },
        };
      }
      
      byPlatform[platform].sessions++;
      
      if (session.summary) {
        byPlatform[platform].interactions += session.summary.total || 0;
        byPlatform[platform].natural += session.summary.natural || 0;
        byPlatform[platform].fallback += session.summary.fallback || 0;
        
        if (session.summary.breakdown) {
          Object.keys(session.summary.breakdown).forEach(key => {
            if (byPlatform[platform].breakdown[key] !== undefined) {
              byPlatform[platform].breakdown[key] += session.summary.breakdown[key] || 0;
            }
          });
        }
      }
    });
    
    // Calcular percentuais
    Object.keys(byPlatform).forEach(platform => {
      const p = byPlatform[platform];
      const total = p.interactions;
      p.naturalPercentage = total > 0 ? ((p.natural / total) * 100).toFixed(1) : '0.0';
      p.fallbackPercentage = total > 0 ? ((p.fallback / total) * 100).toFixed(1) : '0.0';
      p.meetsTarget = parseFloat(p.naturalPercentage) >= 95;
    });
    
    return byPlatform;
  }

  /**
   * Exporta dados para JSON (para análise externa)
   */
  exportData() {
    return JSON.stringify({
      exportedAt: new Date().toISOString(),
      sessions: this.sessions,
      aggregated: this.getAggregatedStats(),
    }, null, 2);
  }

  /**
   * Limpa todas as sessões
   */
  clear() {
    this.sessions = [];
    this.currentSession = null;
  }
}

// Instância global
const voiceDiagnostics = new VoiceDiagnostics();

export default voiceDiagnostics;
export { VoiceDiagnostics };
